"use client";
import { useState } from 'react'

import Link from 'next/link';

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Phone, Mail, Clock, MapPin, CircleCheck as CheckCircle, Building2, Hash } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { submitContactMessage } from '@/lib/api'
import { siteConfig } from '@/data/siteConfig'

const schema = z.object({
  customer_name: z.string().min(2, 'Name is required'),
  email: z.string().email('Valid email required'),
  company: z.string().optional(),
  message: z.string().min(10, 'Please provide more detail'),
})
type FormData = z.infer<typeof schema>

export function ContactPage() {
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  async function onSubmit(data: FormData) {
    setSubmitting(true)
    try {
      await submitContactMessage({
        customer_name: data.customer_name,
        email: data.email,
        company: data.company ?? '',
        message: data.message,
      })
      setSubmitted(true)
    } catch {
      toast.error('Failed to send message. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      

      {/* ContactPage JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        '@id': 'https://jamnagarmetals.com/contact',
        name: 'Contact Jamnagar Metals',
        url: 'https://jamnagarmetals.com/contact',
        description: 'Contact Jamnagar Metals for bulk hardware enquiries, export quotes, and technical specifications.',
        publisher: { '@id': 'https://jamnagarmetals.com/#organization' },
        mainEntity: {
          '@type': 'Organization',
          '@id': 'https://jamnagarmetals.com/#organization',
          name: 'Jamnagar Metals',
          telephone: siteConfig.phone,
          email: siteConfig.email,
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Jamnagar',
            addressRegion: 'Gujarat',
            addressCountry: 'IN',
          },
        },
      }) }} />

      {/* Header */}
      <div className="py-12 px-4" style={{ backgroundColor: '#1C1E24' }}>
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl font-bold text-white mb-2">Contact Us</h1>
          <p className="text-white/60">We respond to all enquiries within 1 business day.</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Left: Contact Details */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div>
              <h2 className="text-lg font-semibold mb-4">Get in Touch</h2>
              <div className="flex flex-col gap-4">
                <a href={`tel:${siteConfig.phone}`} className="flex items-start gap-3 group">
                  <Phone className="h-5 w-5 mt-0.5 flex-shrink-0 transition-colors" style={{ color: '#C79A5C' }} />
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Phone</p>
                    <p className="font-medium group-hover:underline">{siteConfig.phone}</p>
                  </div>
                </a>
                <a href={`mailto:${siteConfig.email}`} className="flex items-start gap-3 group">
                  <Mail className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#C79A5C' }} />
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Email</p>
                    <p className="font-medium group-hover:underline">{siteConfig.email}</p>
                  </div>
                </a>
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#C79A5C' }} />
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Working Hours</p>
                    <p className="font-medium">{siteConfig.workingHours}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Replies to messages received outside hours are sent on the next working day.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#C79A5C' }} />
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Corporate office</p>
                    <p className="font-medium">{siteConfig.location}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#C79A5C' }} />
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Manufacture plant 1</p>
                    <p className="font-medium">{siteConfig.manufacturePlant1}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#C79A5C' }} />
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">Manufacture plant 2</p>
                    <p className="font-medium">{siteConfig.manufacturePlant2}</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-sm font-semibold mb-3">Quick Actions</h3>
              <div className="flex flex-col gap-2">
                <Link href="/quote">
                  <Button className="w-full text-white text-sm" style={{ backgroundColor: '#C79A5C' }}>
                    Request a Quote
                  </Button>
                </Link>
                <Link href="/quick-inquiry">
                  <Button variant="outline" className="w-full text-sm">
                    Quick SKU Inquiry
                  </Button>
                </Link>
              </div>
            </div>

            <Separator />

            {/* Legal disclosure */}
            <div className="text-xs text-muted-foreground space-y-2">
              <div className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                <span>{siteConfig.legal.entityName}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Hash className="h-3.5 w-3.5 flex-shrink-0" />
                <span>GST: {siteConfig.legal.gst}</span>
              </div>
              <p className="leading-relaxed">{siteConfig.legal.registeredAddress}</p>
              <p className="pt-1 text-muted-foreground/70">{siteConfig.legal.brandStatement}</p>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="lg:col-span-3">
            <h2 className="sr-only">Send a Message</h2>
            {submitted ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center py-16">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4" style={{ backgroundColor: 'rgba(46,109,164,0.1)' }}>
                    <CheckCircle className="h-7 w-7" style={{ color: '#C79A5C' }} />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Message Sent!</h3>
                  <p className="text-muted-foreground">We'll get back to you within 1 business day.</p>
                </div>
              </div>
            ) : (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Send a Message</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="c-name">Name *</Label>
                        <Input id="c-name" {...register('customer_name')} className="mt-1" placeholder="Full name" />
                        {errors.customer_name && <p className="text-xs text-destructive mt-1">{errors.customer_name.message}</p>}
                      </div>
                      <div>
                        <Label htmlFor="c-email">Email *</Label>
                        <Input id="c-email" type="email" {...register('email')} className="mt-1" placeholder="you@company.com" />
                        {errors.email && <p className="text-xs text-destructive mt-1">{errors.email.message}</p>}
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="c-company">Company</Label>
                      <Input id="c-company" {...register('company')} className="mt-1" placeholder="Optional" />
                    </div>
                    <div>
                      <Label htmlFor="c-message">Message *</Label>
                      <Textarea id="c-message" {...register('message')} rows={5} className="mt-1" placeholder="Product inquiry, bulk order request, technical question..." />
                      {errors.message && <p className="text-xs text-destructive mt-1">{errors.message.message}</p>}
                    </div>
                    <Button type="submit" disabled={submitting} className="text-white" style={{ backgroundColor: '#C79A5C' }}>
                      {submitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
